import type { ColumnConfig } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import type { GhanttColumnDef } from '@/shared/ghantt';

export const GHANTT_DEFAULT_COLUMN_ID = '__imj_gantt__';

/** Ancho máximo por defecto de la columna Gantt (px). Sustituible con `gantt.column.maxWidth` o `timeline.columnMaxWidthPx`. */
export const DEFAULT_GHANTT_COLUMN_MAX_WIDTH_PX = 560;

/**
 * Inserta la columna sintética `type: 'gantt'` en la lista de columnas de la tabla base.
 */
export function mergeGhanttColumns<T>(
  columns: ColumnConfig<T>[],
  gantt: GhanttColumnDef<T>,
): ColumnConfig<T>[] {
  const { column, data } = gantt;
  const id = column.id ?? GHANTT_DEFAULT_COLUMN_ID;
  const resolvedMaxWidth: ColumnConfig<T>['maxWidth'] =
    column.maxWidth !== undefined
      ? column.maxWidth
      : (gantt.timeline.columnMaxWidthPx ?? DEFAULT_GHANTT_COLUMN_MAX_WIDTH_PX);

  const ganttColumn: ColumnConfig<T> = {
    id,
    header: column.header,
    type: 'gantt',
    accessor: data.accessor as ColumnConfig<T>['accessor'],
    sortable: false,
    filterable: false,
    /** Por defecto sin fijar: la cabecera ocupa el 100% del ancho con dos pisos temporales. */
    lockable: column.lockable ?? false,
    locked: column.locked,
    width: column.width ?? 360,
    minWidth: column.minWidth ?? 0,
    maxWidth: resolvedMaxWidth,
    contentAlign: 'left',
    /** Línea vertical inicial: separa la columna Gantt de la anterior (cabecera y celdas). */
    headerClassName: cn('ui-border-l ui-border-l-default', column.headerClassName),
    /**
     * `ui-px-0`: mismo ancho interior que la cabecera Gantt (`HeaderCell` sin padding horizontal);
     * `ui-align-top`: alinea la rejilla con la cabecera (`vertical-align: top`).
     */
    cellClassName: cn(
      'ui-border-l ui-border-l-default !ui-px-0 ui-align-top ui-box-border',
      column.cellClassName,
    ),
  };

  if (column.placement === 'start') {
    return [ganttColumn, ...columns];
  }
  return [...columns, ganttColumn];
}
